// Sample blog posts
// export const blogsData = [
//   {
//     id: 1,
//     slug: 'building-scalable-applications',
//     title: 'Building Scalable Web Applications with React',
//     excerpt: 'Learn how to build scalable and maintainable web applications using React and modern development practices.',
//     date: 'Jan 15, 2024',
//     url: 'nvs.dev/blog/building-scalable-applications',
//     tags: ['React', 'JavaScript', 'Web Development'],
//     content: `# Building Scalable Web Applications with React

// ## Introduction

// Creating scalable web applications requires careful planning and the right tools. In this post, we'll explore best practices for building React applications that can grow with your needs.

// ## Key Principles

// ### 1. Component Architecture
// - Keep components small and focused
// - Use composition over inheritance
// - Implement proper state management

// ### 2. Performance Optimization
// - Lazy loading and code splitting
// - Memoization with React.memo
// - Virtual scrolling for large lists

// ### 3. Testing Strategy
// - Unit tests for components
// - Integration tests for user flows
// - End-to-end testing with Playwright

// ## Code Example

// \`\`\`javascript
// import React, { lazy, Suspense } from 'react';

// const LazyComponent = lazy(() => import('./LazyComponent'));

// function App() {
//   return (
//     <Suspense fallback={<div>Loading...</div>}>
//       <LazyComponent />
//     </Suspense>
//   );
// }
// \`\`\`

// ## Conclusion

// Building scalable applications is an ongoing journey. By following these principles and continuously learning, you can create robust applications that stand the test of time.
// `,
//   },
//   {
//     id: 2,
//     slug: 'mastering-tailwind-css',
//     title: 'Mastering Tailwind CSS for Modern UI Design',
//     excerpt: 'A comprehensive guide to using Tailwind CSS for creating beautiful and responsive user interfaces.',
//     date: 'Dec 28, 2023',
//     url: 'nvs.dev/blog/mastering-tailwind-css',
//     tags: ['CSS', 'Tailwind', 'Design'],
//     content: `# Mastering Tailwind CSS for Modern UI Design

// ## Why Tailwind CSS?

// Tailwind CSS is a utility-first CSS framework that provides low-level utility classes to build custom designs without leaving your HTML.

// ## Benefits

// 1. **Rapid Development**: Build faster with pre-defined utility classes
// 2. **Consistency**: Maintain design consistency across your application
// 3. **Customization**: Easily customize the design system to match your brand
// 4. **Responsive Design**: Built-in responsive modifiers make mobile-first design simple

// ## Getting Started

// \`\`\`bash
// npm install tailwindcss
// npx tailwindcss init
// \`\`\`

// ## Best Practices

// - Use @apply for repeating patterns
// - Leverage the JIT compiler for better performance
// - Create custom utilities in your config file
// - Use plugins to extend functionality

// ## Example Component

// \`\`\`jsx
// <div className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
//   <h2 className="text-2xl font-bold text-gray-800 mb-4">Card Title</h2>
//   <p className="text-gray-600">Card content goes here</p>
// </div>
// \`\`\`

// Tailwind CSS empowers developers to create stunning UIs with unprecedented speed and flexibility.
// `,
//   },
//   {
//     id: 3,
//     slug: 'api-design-best-practices',
//     title: 'RESTful API Design: Best Practices and Patterns',
//     excerpt: 'Essential guidelines for designing clean, maintainable, and scalable RESTful APIs.',
//     date: 'Nov 10, 2023',
//     url: 'nvs.dev/blog/api-design-best-practices',
//     tags: ['API', 'Backend', 'REST'],
//     content: `# RESTful API Design: Best Practices and Patterns

// ## Introduction to REST

// REST (Representational State Transfer) is an architectural style for designing networked applications.

// ## Core Principles

// ### 1. Resource-Based URLs
// \`\`\`
// GET    /api/users          # Get all users
// GET    /api/users/:id      # Get specific user
// POST   /api/users          # Create new user
// PUT    /api/users/:id      # Update user
// DELETE /api/users/:id      # Delete user
// \`\`\`

// ### 2. HTTP Methods
// Use appropriate HTTP methods for different operations:
// - GET: Retrieve data
// - POST: Create new resources
// - PUT/PATCH: Update resources
// - DELETE: Remove resources

// ### 3. Status Codes
// - 200: Success
// - 201: Created
// - 400: Bad Request
// - 401: Unauthorized
// - 404: Not Found
// - 500: Server Error

// ## Error Handling

// \`\`\`json
// {
//   "error": {
//     "code": "VALIDATION_ERROR",
//     "message": "Invalid input data",
//     "details": ["Email is required", "Password must be at least 8 characters"]
//   }
// }
// \`\`\`

// ## Versioning

// Always version your APIs:
// - URL versioning: \`/api/v1/users\`
// - Header versioning: \`Accept: application/vnd.api.v1+json\`

// Following these practices ensures your API is maintainable and easy to use for developers.
// `,
//   },
// ];

export const projectsData = [{
  id: 'scm-backorder',
  slug: 'scm-backorder',
  title: 'SCM Backorder Prediction',
  credential: 'Published research · Nitish Venkatesh S.R · IJME, 2022',
  tags: ['Python','LightAutoML','LightGBM','CatBoost','scikit-learn'],
  deepDive: {
    title: 'SCM Backorder Prediction',
    subtitle: 'Supply Chain Logistics with AutoML',
    description: 'Predicting product backorders using LightAutoML on a highly imbalanced dataset (1:148 ratio). Automated pipeline handles preprocessing, feature selection, and tuning — achieving 97.1% ROC-AUC where traditional models struggled to exceed 63%.',
    architecture: [
      { label:'Raw Data', sub:'1.68M × 23', icon:'db', color:'#64D2FF' },
      { label:'LAMA Preprocess', sub:'Auto typing + Gini', icon:'gear', color:'#FF9F0A' },
      { label:'Feature Select', sub:'Importance cutoff', icon:'filter', color:'#BF5AF2' },
      { label:'Linear L2', sub:'Regularized', icon:'model', color:'#007AFF' },
      { label:'LightGBM', sub:'Gradient boost', icon:'model', color:'#007AFF' },
      { label:'CatBoost', sub:'Cat. boost', icon:'model', color:'#007AFF' },
      { label:'Ensemble', sub:'Weighted blend', icon:'merge', color:'#30D158' },
      { label:'Output', sub:'Yes / No', icon:'out', color:'#30D158' },
    ],
    hyperparameters: [
      { key:'task', value:'binary', type:'str' },
      { key:'timeout', value:'7200s', type:'num' },
      { key:'n_folds', value:'5', type:'num' },
      { key:'n_threads', value:'8', type:'num' },
      { key:'weight_0', value:'0.50', type:'num' },
      { key:'weight_1', value:'74.73', type:'num' },
      { key:'random_state', value:'42', type:'num' },
      { key:'test_size', value:'0.2', type:'num' },
    ],
    metrics: {
      rocAuc:97.1, f1:69.95, precision:70.55, recall:69.4,
      trainingLoss:[{epoch:1,loss:0.82,val:0.79},{epoch:5,loss:0.64,val:0.63},{epoch:10,loss:0.51,val:0.52},{epoch:15,loss:0.42,val:0.44},{epoch:20,loss:0.36,val:0.39},{epoch:25,loss:0.31,val:0.35},{epoch:30,loss:0.27,val:0.33},{epoch:35,loss:0.24,val:0.31},{epoch:40,loss:0.22,val:0.29},{epoch:45,loss:0.19,val:0.28},{epoch:50,loss:0.17,val:0.27}],
      confusionMatrix:{labels:['No Backorder','Backorder'],matrix:[[1635200,8420],[4180,7660]]},
      featureImportance:[{feature:'national_inv',importance:2.45},{feature:'forecast_3_month',importance:1.32},{feature:'forecast_6_month',importance:1.05},{feature:'perf_12_month_avg',importance:0.87},{feature:'sales_1_month',importance:0.82},{feature:'lead_time',importance:0.78},{feature:'sales_3_month',importance:0.72},{feature:'forecast_9_month',importance:0.68},{feature:'sales_6_month',importance:0.61},{feature:'min_bank',importance:0.55},{feature:'sales_9_month',importance:0.48},{feature:'pieces_past_due',importance:0.35},{feature:'in_transit_qty',importance:0.31},{feature:'local_bo_qty',importance:0.22},{feature:'perf_6_month_avg',importance:0.18}],
      comparativeResults:[{model:'LightAutoML',f1:69.95,roc:97.1,time:66},{model:'Random Forest',f1:35,roc:63,time:5},{model:'Logistic Reg.',f1:12,roc:55,time:3},{model:'SVM',f1:1,roc:50,time:90},{model:'kNN',f1:1,roc:56,time:183}],
      thresholdAnalysis:[{threshold:0.5,precision:62.11,recall:81.38,f1:67.31},{threshold:0.8,precision:70.55,recall:69.4,f1:69.95}],
    },
    notebookUrl:'https://colab.research.google.com/', reportUrl:'#', datasetUrl:'https://www.kaggle.com/',
  },
  notebookUrl:'https://colab.research.google.com/',
  githubUrl:'https://github.com/niccolopaganini/Supply-Chain-Logistics-Management-with-Automated-Machine-Learning',
  reportUrl:'#',
  story: {
    credential: 'Published research · Nitish Venkatesh S.R · IJME, 2022',
    problem: 'Manufacturers lose millions when they can\'t tell which products are about to run out before customers order them.',
    stakes: 'A single missed backorder cascades: delayed shipments, broken SLAs, lost customers. Most forecasting models fail here because backorders are rare — only 1 in 148 products actually goes on backorder, and standard ML treats the signal as noise.',
    approach: 'I built an AutoML pipeline that learns from 1.68 million historical inventory records and flags at-risk products before they stock out. Instead of hand-tuning dozens of models, the system automatically selects features, balances the rare-event problem, and blends three specialized models into one prediction.',
    bigNumbers: [
      { value:'97.1%', label:'ROC-AUC', sub:'vs 63% for Random Forest' },
      { value:'2×', label:'Better F1', sub:'than the next-best model' },
      { value:'66 min', label:'Full training', sub:'on 1.68M rows, automated' },
    ],
    soWhat: 'A supply chain team using this model would catch nearly every true backorder before it happens, with runtime short enough to retrain weekly as demand shifts. It replaces gut-feel reorder decisions with a signal that actually scales.',
    cta: { label:'Read the published paper', url:'#' },
  },
}];

export const githubReposData = [
  { id:1, name:'Supply Chain Management', description:'LightAutoML pipeline for imbalanced supply-chain backorder classification.', language:'Python', stars:0, url:'https://github.com/niccolopaganini/Supply-Chain-Logistics-Management-with-Automated-Machine-Learning' },
  { id:2, name:'PM 2.5 Prediction in Cali', description:'Spatiotemporal PM2.5 prediction using CNN-LSTM + XGBoost.', language:'Python', stars:0, url:'https://github.com/niccolopaganini/Pm2.5-prediction-at-an-AQMD-station-using-nearby-station-s-data' },
  { id:3, name:'portfolio-os (coming soon!)', description:'React 19 + Framer Motion OS-style portfolio.', language:'JavaScript', stars:0, url:'https://github.com/niccolopaganini' },
  { id:4, name:'ml-notebooks (coming soon!)', description:'Curated Jupyter notebooks from coursework and research.', language:'Jupyter', stars:0, url:'https://github.com/niccolopaganini' },
];

export const certificationsData = [
  { id:1, title:'Oracle Cloud Infrastructure 2025 Certified Generative AI Professional', issuer:'Oracle', date:'2025', imageUrl:'https://catalog-education.oracle.com/ords/certview/sharebadge?id=585D37AB35506AE7BD452B54581FC51AE07770FD0DC0EBE41B1BA44E9954065A#' },
  { id:2, title:'McKinsey.org Forward Program', issuer:'McKinsey & Company', date:'2022', imageUrl:null },
  { id:3, title:'Fundamentals of Deep Learning', issuer:'NVIDIA', date:'2021', imageUrl:null },
  { id:4, title:'Full Stack Web Development', issuer:'freeCodeCamp', date:'2021', imageUrl:null },
];

// export const certificationsData = [
//   {
//     id: 1,
//     title: 'Oracle Cloud Infrastructure 2025 Certified Generative AI Professional',
//     issuer: 'Oracle',
//     date: '2025',
//     imageUrl: "https://catalog-education.oracle.com/ords/certview/sharebadge?id=585D37AB35506AE7BD452B54581FC51AE07770FD0DC0EBE41B1BA44E9954065A#", // Will show trophy icon
//   },
//   {
//     id: 2,
//     title: 'McKinsey.org Forward Program',
//     issuer: 'McKinsey & Company',
//     date: '2022',
//     imageUrl: null,
//   },
//   {
//     id: 3,
//     title: 'Fundamentals of Deep Learning',
//     issuer: 'NVIDIA',
//     date: '2021',
//     imageUrl: null,
//   },
//   {
//     id: 4,
//     title: 'Full Stack Web Development',
//     issuer: 'freeCodeCamp',
//     date: '2021',
//     imageUrl: null,
//   },
  // {
  //   id: 5,
  //   title: 'Google Cloud Professional',
  //   issuer: 'Google Cloud',
  //   date: '2023',
  //   imageUrl: null,
  // },
  // {
  //   id: 6,
  //   title: 'MongoDB Certified Developer',
  //   issuer: 'MongoDB University',
  //   date: '2022',
  //   imageUrl: null,
  // },
// ];
